
import * as React from 'react';
import { graft, Patch, Delete, ReplacePatch } from '../../utilities/Graft';
import { Fragment } from '../../utilities/components/Fragment';
import { IUpdateActionOptions, ActionHost, Action as BaseAction } from '../action/Action';
import { IRenderProps, renderChildrenWithProps } from '../../utilities/components/RenderProps';
import { IOnExecuteProps } from '../commandSet/CommandSet';
import { IItemCache } from '../../models/IItemCacheObserver';
import { connectToItemCacheLinkFacet } from '../decorators/connectToItemCache';
import { IFaceted } from '../../models/Facet';

/**
 * @public
 */
export type IActionInput = React.ComponentType<{}> | JSX.Element | null;

/**
 * @internal
 */
export function renderAction(actionInput: IActionInput | undefined): JSX.Element {
    const ActionInput = actionInput;

    return typeof ActionInput === 'function' ? (
        <ActionInput />
    ) : ActionInput || <BaseAction />;
}

/**
 * @internal
 */
export function evaluateAction(itemCache: IItemCache, actionInput?: IActionInput): JSX.Element {
    const element = renderAction(actionInput);

    if (typeof element.type === 'function') {
        const connectToItemCacheLink = connectToItemCacheLinkFacet.unpack(element.type as {} as IFaceted);

        if (connectToItemCacheLink) {
            const Component = connectToItemCacheLink.component;

            const componentProps = connectToItemCacheLink.mapItemCacheToProps(itemCache, {
                ...element.props
            });

            return (
                <Component
                    { ...element.props }
                    { ...componentProps }
                />
            );
        }
    }

    return element;
}

/**
 * @internal
 */
export const INITIAL_ACTION_STATE: IActionStateProps = {
    isAvailable: false,
    executingCount: 0,
    execute: async () => { /* Nothing */ }
};

/**
 * The state of the action passed as a render prop.
 * @public
 */
export interface IActionStateProps {
    /**
     * Whether or not the action is currently available.
     */
    isAvailable: boolean;
    /**
     * Whether or not the action is in a toggled state. Undefined means it does not support toggle behavior.
     */
    isToggled?: boolean;
    /**
     * Number of current active executons of the action.
     */
    executingCount: number;
    /**
     * Executes the action and waits for it to resolve.
     */
    execute: (props?: IOnExecuteProps) => Promise<void>;
}

/**
 * @public
 */
export interface IWithActionSetStateProps<K extends string = string> {
    /**
     * A mapping of provided keys for actions and their current states.
     * These keys should match those passed into props.
     */
    actions: {
        [P in K]?: IActionStateProps | undefined;
    };
}

/**
 * @public
 */
export interface IWithActionSetProps<K extends string = string> extends IRenderProps<IWithActionSetStateProps<K>> {
    /**
     * A mapping of provided keys for actions to their rendered instances.
     * The keys will be used when reporting the status, either via the 'children' render prop or
     * via onActionsUpdated.
     *
     * @example
     * ```tsx
     * <WithActionSet
     *  actions={
     *      {
     *          'a': <UploadAction />
     *          'b': <ItemShareAction itemKey={ itemKey } />
     *      }
     *  }
     * />
     * ```
     */
    actions: {
        [P in K]: IActionInput;
    };
    /**
     * A callback invoked whenever any action updates, which provides the current states of all actions.
     * Use this callback to map action state into the state of a host component.
     */
    onActionsUpdated?: (actionSetProps: IWithActionSetStateProps<K>) => void;
}

/**
 * @public
 */
export interface IWithActionSetState<K extends string = string> {
    actions: {
        [P in K]?: IActionStateProps;
    };
}

/**
 * Component which evaluates the states of multiple Action instances simultaneously and
 * provides hooks to get their availability and execution callback.
 * @public
 */
export class WithActionSet<K extends string = string> extends React.Component<IWithActionSetProps<K>, IWithActionSetState<K>> {
    private _state: IWithActionSetState<K>;
    private _lastRenderedState?: IWithActionSetState<K>;

    constructor(props: IWithActionSetProps<K>) {
        super(props);

        this.state = this._state = {
            actions: {}
        };

        (this._content as { displayName?: string; }).displayName = 'WithActionSet.Callback';
    }

    public render(): JSX.Element {
        const {
            actions
        } = this.props;

        const Content = this._content;

        return (
            <Fragment>
                <Fragment hidden={ true }>
                    {
                        (Object.keys(actions) as K[]).map((key: K) => {
                            return (
                                <ActionHost
                                    key={ key }
                                    actionKey={ key }
                                    preview={ this._preview }
                                    update={ this._update }
                                    remove={ this._remove }
                                >
                                    { renderAction(actions[key]) }
                                </ActionHost>
                            );
                        })
                    }
                </Fragment>
                <Content>{ this.props.children }</Content>
            </Fragment>
        );
    }

    private _preview = (options: IUpdateActionOptions, key: string): void => {
        const actions: {[P in K]?: Patch<IActionStateProps>; } = {};
        actions[key as K] = new ReplacePatch<IActionStateProps>(options);

        // Use graft to debounce the state when reporting to the caller.
        this._state = graft(this._state, {
            actions: actions
        });
    }

    private _update = (options: IUpdateActionOptions, key: string): void => {
        const actions: {[P in K]?: Patch<IActionStateProps>; } = {};
        actions[key as K] = new ReplacePatch<IActionStateProps>(options);

        // Use graft to debounce the state when reporting to the caller.
        this._state = graft(this._state, {
            actions: actions
        });

        if (this._state !== this._lastRenderedState) {
            this.setState(this._state);
        }

        const {
            onActionsUpdated
        } = this.props;

        if (onActionsUpdated) {
            onActionsUpdated(this._state);
        }
    }

    private _remove = (key: string): void => {
        const actions: {[P in K]?: Delete<IActionStateProps>; } = {};
        actions[key as K] = new Delete<IActionStateProps>();

        // Use graft to debounce the state when reporting to the caller.
        this._state = graft(this._state, {
            actions: actions
        });

        if (this._state !== this._lastRenderedState) {
            this.setState(this._state);
        }

        const {
            onActionsUpdated
        } = this.props;

        if (onActionsUpdated) {
            onActionsUpdated(this._state);
        }
    }

    private _content = (props: IRenderProps<IWithActionSetStateProps>): JSX.Element | null => {
        // Normally, mutating state in a React component is not a good idea.
        // However, this just marks that a subsequent update may be unnecessary
        // and does not cause unwanted side-effects.
        this._lastRenderedState = this._state;

        return renderChildrenWithProps(props.children, this._state);
    }
}

/**
 * The props for the WithAction render prop component.
 * @public
 */
export interface IWithActionProps extends IRenderProps<IActionStateProps> {
    action?: IActionInput;
    onActionUpdated?: (actionProps: IActionStateProps) => void;
    actionKey?: string;
}

/**
 * Component which passes the state of a chosen action to a render prop.
 * @public
 */
export class WithAction extends React.Component<IWithActionProps, IActionStateProps> {
    /**
     * WithAction is a fairly high-traffic component with a bunch of implications
     * for rendering. Store local fields in order to avoid costly re-renders when
     * evaluating actions.
     */
    private _state: IActionStateProps;
    private _lastRenderedState?: IActionStateProps;

    private _waitingForPreview: boolean | undefined;

    constructor(props: IWithActionProps) {
        super(props);

        this.state = this._state = INITIAL_ACTION_STATE;

        this._waitingForPreview = true;

        (this._content as { displayName?: string; }).displayName = 'WithAction.Callback';
    }

    public render(): JSX.Element {
        const Content = this._content;

        return (
            <Fragment>
                {
                    this.props.action ? (
                        <Fragment hidden={ true }>
                            <ActionHost
                                actionKey={ this.props.actionKey || '' }
                                preview={ this._preview }
                                update={ this._update }
                                remove={ this._remove }
                            >
                                { renderAction(this.props.action) }
                            </ActionHost>
                        </Fragment>
                    ) : null
                }
                {
                    // By rendering Content as an element, it will be *queued* for render,
                    // allowing the ActionHost and its children to be constructed and provided with props
                    // before Content is subsequently rendered.
                    // This provides an opportunity to get the correct initial action state to
                    // the callback before first render, saving performance!
                }
                <Content>{ this.props.children }</Content>
            </Fragment>
        );
    }

    public componentWillReceiveProps(): void {
        this._waitingForPreview = true;
    }

    public componentDidMount(): void {
        this._waitingForPreview = false;
    }

    public componentDidUpdate(): void {
        this._waitingForPreview = false;
    }

    private _preview = (options: IUpdateActionOptions, actionKey: string): void => {
        this._state = options;
    }

    private _update = (options: IUpdateActionOptions, actionKey: string): void => {
        if (this._waitingForPreview) {
            if (this._lastRenderedState !== options) {
                throw new Error('Did not render the previewed state!');
            }

            this._waitingForPreview = undefined;
        }

        this._state = options;

        if (this._state !== this._lastRenderedState) {
            this.setState(this._state);
        }

        const {
            onActionUpdated
        } = this.props;

        if (onActionUpdated) {
            onActionUpdated(this._state);
        }
    }

    private _remove = (actionKey: string): void => {
        this._waitingForPreview = false;

        this._state = INITIAL_ACTION_STATE;

        if (this._state !== this._lastRenderedState) {
            this.setState(this._state);
        }

        const {
            onActionUpdated
        } = this.props;

        if (onActionUpdated) {
            onActionUpdated(this._state);
        }
    }

    private _content = (props: IRenderProps<IActionStateProps>): JSX.Element | null => {
        // Normally, mutating state in a React component is not a good idea.
        // However, this just marks that a subsequent update may be unnecessary
        // and does not cause unwanted side-effects.
        this._lastRenderedState = this._state;

        return renderChildrenWithProps(props.children, this._state);
    }
}
